import React from 'react'
import { moneyOrBlank } from './overview'
import DetailNote from './DetailNote'

// ─────────────────────────────────────────────────────────────────────────────
//   Impuestos — dos líneas que nunca se suman
//
//   `tax.paid_actual` es dinero que salió de la empresa. `tax.reserve_estimated`
//   es un pronóstico sobre dinero que NO se ha movido, calculado a un
//   porcentaje que ningún contable ha confirmado. Son dos clases de número
//   distintas y el backend lo dice en su propio `detail`.
//
//   De ahí la forma de este componente, que es lo único que impide el error:
//   **no hay un encabezado "Taxes" que las agrupe**, porque un encabezado
//   común es ya la invitación a sumarlas — y el esquema deliberadamente no
//   expone ningún campo que las sume. Son dos bloques hermanos, cada uno con
//   su propio título completo, legible por sí solo y sin el otro delante.
//   Cualquier refactor que los meta bajo un `<h3>Taxes</h3>` para "ahorrar
//   espacio" reintroduce exactamente el bug.
// ─────────────────────────────────────────────────────────────────────────────

// El color aquí es la segunda mitad de lo que impide sumar las dos cifras: el
// verde es dinero que salió y el ámbar es un pronóstico sobre dinero que no se
// ha movido, y dos tarjetas de colores distintos no se leen como dos sumandos
// de lo mismo. `tone` tiñe borde, fondo suave y cifra a la vez para que la
// distinción se vea de un vistazo y no haya que leer los dos títulos enteros.
function TaxBlock({ title, value, kindOf, tone, toneBg }) {
  return (
    <div
      className="card"
      style={{ margin: 0, minWidth: 0, borderLeft: `3px solid ${tone}`, background: toneBg }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: tone, marginTop: 6 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
        {kindOf}
      </div>
    </div>
  )
}

export default function TaxLines({ tax, loading }) {
  const reserve = tax?.reserve_estimated

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        className="cf-tax"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}
      >
        <TaxBlock
          tone="var(--green)"
          toneBg="var(--green-bg)"
          title="Tax already paid on payroll"
          value={loading ? '—' : moneyOrBlank(tax?.paid_actual)}
          kindOf="Money that left the company. Counted only where the cash entry actually moved."
        />
        <TaxBlock
          tone="var(--amber)"
          toneBg="var(--amber-bg)"
          title="Tax reserve set aside — estimated, not paid"
          value={
            loading
              ? '—'
              // null y 0 son respuestas opuestas aquí: null es "no había
              // política vigente, no se pudo calcular" y 0 es "se calculó y no
              // había excedente contra el que reservar". Un '$0.00' en el
              // primer caso afirmaría lo segundo.
              : reserve === null || reserve === undefined
                ? <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Not computed — no policy in force
                  </span>
                : moneyOrBlank(reserve)
          }
          kindOf="A forecast about money that has not moved, at a percentage nobody has confirmed."
        />
      </div>

      {/* DEBAJO de los dos y a todo el ancho, no como una tercera columna al
          lado. En la rejilla se leía como un tercer tipo de impuesto —tres
          columnas hermanas, y la del medio con una cifra— cuando es un pie que
          aplica a los dos por igual.

          La línea de resumen es rótulo y la escribe la pantalla; el porqué
          entero es el `detail` del endpoint, literal y plegado. Reescribirlo
          aquí sería una segunda versión que se desincroniza de la regla.

          Y sigue SIN agrupar nada: lo que no puede aparecer nunca es un
          `<h3>Taxes</h3>` por encima de los dos bloques — ver la cabecera del
          fichero. Un pie debajo no es un encabezado encima. */}
      <DetailNote
        style={{ marginTop: 10 }}
        summary="Two kinds of number — never added together, never under one heading"
        detail={tax?.detail}
      />
    </div>
  )
}
